export type DetailsFormProblem =
  | 'CATEGORY_MISSING'
  | 'TAG_MISSING'
  | 'DESCRIPTION_MISSING'
  | 'DESCRIPTION_TOO_LONG'
  | 'CONTROLS_MISSING'
  | 'CONTROLS_TOO_LONG'
  | 'PLAY_URL_INVALID'
  | 'APPSTORE_URL_INVALID'
  | 'STEAM_URL_INVALID'
  | 'APP_STORE_DOWNLOADS_INVALID'
  | 'PLAY_STORE_DOWNLOADS_INVALID'
  | 'STEAM_DOWNLOADS_INVALID'
  | 'COVER_IMAGES_MISSING'
  | 'COVER_IMAGE_MISSING16x9'
  | 'COVER_IMAGE_MISSING2x3'
  | 'COVER_IMAGE_MISSING1x1'
  | 'VIDEO_UPLOADING' // videos are still uploading
  | 'LANDSCAPE_VIDEO_MISSING';

const DETAILS_FORM_ERROR_LABELS: Record<DetailsFormProblem, string> = {
  CATEGORY_MISSING: 'Category is missing',
  TAG_MISSING: 'Tag is missing',
  DESCRIPTION_MISSING: 'Description is missing',
  DESCRIPTION_TOO_LONG: 'Description is too long',
  CONTROLS_MISSING: 'Controls are missing',
  CONTROLS_TOO_LONG: 'Controls are too long',
  PLAY_URL_INVALID: 'Play Store URL is invalid',
  APPSTORE_URL_INVALID: 'App Store URL is invalid',
  STEAM_URL_INVALID: 'Steam URL is invalid',
  PLAY_STORE_DOWNLOADS_INVALID: 'Play Store downloads is invalid',
  APP_STORE_DOWNLOADS_INVALID: 'App Store downloads is invalid',
  STEAM_DOWNLOADS_INVALID: 'Steam downloads is invalid',
  COVER_IMAGES_MISSING: 'Cover images are missing',
  COVER_IMAGE_MISSING16x9: 'Landscape 16:9 cover image is missing',
  COVER_IMAGE_MISSING2x3: 'Portrait 2:3 cover image is missing',
  COVER_IMAGE_MISSING1x1: 'Square 1:1 cover image is missing',
  VIDEO_UPLOADING: 'Video is still uploading',
  LANDSCAPE_VIDEO_MISSING: 'Landscape video is missing',
} as const;

/** These block updating/submiting a submission */
export type BaseFormProblem =
  | 'NAME_MISSING'
  | 'NAME_INVALID'
  | 'GAME_LOADER_TYPE_MISSING'
  | 'HTML5_FILES_MISSING'
  | 'IFRAME_INVALID_URL'
  | 'UNITY_FILES_MISSING'
  | 'APS_INFO_MISSING'
  | 'APS_UNITY_CUSTOM_NAME_MISSING'
  | 'APS_UNITY_CUSTOM_NAME_INVALID'
  | 'GAME_FILE_UPLOAD_IN_PROGRESS'
  | 'TOTAL_FILE_COUNT_EXCEEDED'
  | 'TOTAL_FILE_SIZE_EXCEEDED'
  | 'ORIENTATION_MISSING'
  | 'MULTIPLAYER_INVALID_LOBBY'
  | 'MULTIPLAYER_INVALID_LOBBY_MIN'
  | 'MULTIPLAYER_INVALID_LOBBY_MAX';

/** These are yellow warnings, and they don't prevent you from submitting */
export type BaseFormSoftWarnings = 'TOTAL_FILE_SIZE_TOO_BIG';

export const BASE_FORM_ERROR_LABELS: Record<BaseFormProblem, string> = {
  NAME_MISSING: 'Name is required',
  NAME_INVALID: 'Name is invalid',

  ORIENTATION_MISSING: 'Orientation is required',

  TOTAL_FILE_COUNT_EXCEEDED: 'Too many files uploaded',
  TOTAL_FILE_SIZE_EXCEEDED: 'File size limit exceeded',

  MULTIPLAYER_INVALID_LOBBY: 'Invalid lobby configuration',
  MULTIPLAYER_INVALID_LOBBY_MIN: 'Minimum lobby size invalid',
  MULTIPLAYER_INVALID_LOBBY_MAX: 'Maximum lobby size invalid',

  GAME_LOADER_TYPE_MISSING: 'Game engine is missing',
  GAME_FILE_UPLOAD_IN_PROGRESS: 'Game file upload in progress',
  HTML5_FILES_MISSING: 'HTML5 files are missing',
  APS_INFO_MISSING: 'APS information is missing',
  IFRAME_INVALID_URL: 'Invalid iframe URL',
  UNITY_FILES_MISSING: 'Unity files are missing',
  APS_UNITY_CUSTOM_NAME_MISSING: 'Custom Unity name is missing',
  APS_UNITY_CUSTOM_NAME_INVALID: 'Custom Unity name is invalid',
} as const;

export function getErrorLabels(errors: (BaseFormProblem | DetailsFormProblem)[]): string {
  const allLabels = { ...BASE_FORM_ERROR_LABELS, ...DETAILS_FORM_ERROR_LABELS };
  const errorLabels = errors?.map((error) => allLabels[error]);

  // older submissions may have missing square/portrait cover images
  // inform developers where these can be updated as we get questions about this
  const coverErrors: DetailsFormProblem[] = ['COVER_IMAGE_MISSING1x1', 'COVER_IMAGE_MISSING2x3'];
  const hasCoverErrors = coverErrors.some((error) => errors.includes(error));
  if (hasCoverErrors) {
    errorLabels.push('Covers can be updated in the "Update game info" section');
  }

  return errorLabels?.join(', ');
}
